import { _ftCamField } from '../shared/campaign-field';
import { encounterField } from '../shared/array-types';
import { refuseUnported } from '../janus-engine/unported';
import { campaignLightForClock } from '../shared/campaign-light';
import { planCampaignClock, type ClockAction, type CampaignClockPlan } from '../shared/campaign-clock-plan';
import { ftClockBaseFromArr, ftClockColsFromArr, ftDeriveCampaignClock, ftCampaignTimeAddSeconds } from '../shared/campaign-clock';
import { withAsyncDeadline } from '../shared/async-deadline';

type Row = Record<string, any>;
const W = window as unknown as Row;
const unresolved = new Map<string, Row>();
const inFlight = new Set<string>();
const published = new Set<string>();
const recoveryKey = (cid: string): string => `ft.clock.checkpoint:${W.FateTide?.user?.id ?? ''}:${cid}`;
function heldCampaign(cid: string): Row | undefined { return W.FateTide?.campaignCache?.get(cid); }
function nowFor(campaign: Row): number {
    const stamp = campaign.computed?.campaign?.server_utc_now;
    const offset = W.ftCampaignClock?.serverNowMs?.();
    return typeof offset === 'number' ? offset : stamp ? Date.parse(String(stamp).replace(' ', 'T') + 'Z') : Date.now();
}
function clockRequest(action: ClockAction, cid: string, seconds = 0): Row {
    const campaign = heldCampaign(cid);
    if (!campaign) throw Error('calendar-light:calendar-unavailable');
    if (action === 'play') {
        const encounter = W.FateTide?.encounterCache?.get(campaign.campaigns?.encounter_loaded);
        if (!encounter) throw Error('calendar-light:pull-plan-unavailable');
        if (Number(encounterField(encounter, 'in_combat') ?? 0) === 1) throw Error('in_combat');
    }
    return { campaign_id: cid, action, operation_id: crypto.randomUUID().toUpperCase(), clock_plan: planCampaignClock(campaign, action, nowFor(campaign), seconds) };
}
function applyClockAnswer(answer: Row): void {
    if (!answer.ok) return;
    W.ftApplyCampaignClockDelta?.({ schema: 'janus.time.push.v1', committed: true, operation_id: answer.operation_id,
        campaign_id: answer.campaign_id, encounter_id: '', campaign_delta: answer.campaign_delta, pending_closed: answer.pending_closed });
    if (published.has(answer.operation_id)) return;
    published.add(answer.operation_id);
    if (published.size > 256) published.delete(published.values().next().value!);
    W.ftPushCampaignTruth?.({ type: 'janus_time_truth', campaign_id: answer.campaign_id, operation_id: answer.operation_id,
        command: { schema: 'janus.time.push.v1', committed: true, operation_id: answer.operation_id,
            campaign_id: answer.campaign_id, encounter_id: '', campaign_delta: answer.campaign_delta, pending_closed: answer.pending_closed,
            saved_rolls: answer.saved_rolls, truth_presentation_rows: answer.truth_presentation_rows,
            actor_id: W.FateTide?.user?.id, presentation_user_id: W.FateTide?.user?.id } });
}
/** Lost responses retry the exact decided bytes. A definite CAS miss may replan once from its raw snapshot. */
async function sendClock(payload: Row, keepalive = false): Promise<Row | null> {
    const cid = String(payload.campaign_id);
    if (inFlight.has(cid)) return { ok: false, reason: 'calendar-light:write-in-flight' };
    const command = unresolved.get(cid) ?? payload;
    inFlight.add(cid);
    try {
        if (keepalive && command === payload) command.pagehide = true;
        try { W.localStorage?.setItem(recoveryKey(cid), JSON.stringify(command)); } catch { /* storage may be disabled */ }
        if (keepalive && command === payload) {
            // A closing document cannot wait for an HTTP reply to publish. Retain only
            // this decided operation for browser recovery after navigation/disconnect.
            const after = command.clock_plan.after;
            applyClockAnswer({ ok: true, campaign_id: cid, operation_id: command.operation_id,
                truth_presentation_rows: command.clock_plan.rows, campaign_delta: { columns: {
                ...Object.fromEntries(Object.entries(after.clock).map(([key, value]) => ['campaign_time_' + key, value])),
                campaign_clock_running_since: after.running_since, campaign_light: after.campaign_light,
                campaign_sun_state: after.campaign_sun_state }, sun_transition_minutes: after.sun_transition_minutes } });
        }
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const { response, answer } = await withAsyncDeadline(async signal => {
                    const response = await fetch(`${W.BASE_URL ?? ''}options/tools/campaigns/checkpoint_campaign_time_inc.php`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
                    body: JSON.stringify(command), keepalive, signal });
                    return { response, answer: await response.json() as Row };
                }, 20_000);
                if (response.status >= 500) continue;
                unresolved.delete(cid);
                try { W.localStorage?.removeItem(recoveryKey(cid)); } catch { /* storage may be disabled */ }
                if (answer.ok) applyClockAnswer(answer);
                else if (answer.reason === 'stale' && answer.authoritative) {
                    const campaign = heldCampaign(cid), snapshot = answer.authoritative;
                    if (campaign) {
                        for (const [key, value] of Object.entries(snapshot.clock ?? {})) (campaign.campaigns ??= {})['campaign_time_' + key] = value;
                        if (snapshot.calendar_id !== undefined && snapshot.calendar_id !== _ftCamField(campaign, 'calendar_id'))
                            delete campaign.computed?.campaign?.calendar;
                        for (const key of ['campaign_light', 'campaign_sun_state', 'calendar_id', 'campaign_light_cycle_mode'])
                            if (key in snapshot) campaign.campaigns[key] = snapshot[key];
                        campaign.campaigns.campaign_clock_running_since = snapshot.running_since;
                        (campaign.computed ??= {}).campaign ??= {};
                        campaign.computed.campaign.server_utc_now = snapshot.server_utc_now;
                        W.ftCampaignClock?.captureBlob?.(campaign);
                    }
                }
                // A recovered prior write consumes this click; never apply the requested duration a second time.
                return command === payload ? answer : { ...answer, recovered: true };
            } catch { /* bounded retry of this operation; the durable receipt settles an ambiguous save */ }
        }
        unresolved.set(cid, command);
        return { ok: false, reason: 'calendar-light:outcome-unresolved' };
    } finally { inFlight.delete(cid); }
}
async function commitCalendarClock(input: { campaignId: string; seconds: number; gmLog?: boolean }): Promise<boolean> {
    if (W.ftBaseCommitsPaused?.()) { refuseUnported('engine-version-reload'); return false; }
    if (W.FateTide?.user?.isGM !== true) { refuseUnported('calendar-light:permission'); return false; }
    if (!Number.isSafeInteger(input.seconds)) { refuseUnported('calendar-light:pull-plan-unavailable'); return false; }
    try {
        for (let attempt = 0; attempt < 2; attempt++) {
            const request = clockRequest('adjust', input.campaignId, input.seconds);
            if (input.gmLog) request.clock_plan.rows.push({ roll_type_1: 'gm_pulled_campaign_time', roll_result: Math.abs(input.seconds), roll_private: 0, rolls_full: '' });
            const result = await sendClock(request);
            if (result?.ok) return true;
            if (result?.recovered) { refuseUnported(result.reason); return false; }
            if (result?.reason !== 'stale' || attempt) { refuseUnported(result?.reason ?? 'calendar-light:pull-plan-unavailable'); return false; }
        }
    } catch (error) { refuseUnported(error instanceof Error ? error.message : 'calendar-light:pull-plan-unavailable'); }
    return false;
}
let draining = false;
async function drainCalendarElapsed(cid: string): Promise<void> {
    if (window.top && window.top !== window) return;
    if (W.__ftCalendarElapsedHold === true || draining || W.FateTide?.user?.isGM !== true || W.ftBaseCommitsPaused?.()) return;
    draining = true;
    try {
        let recovered: Row | undefined;
        try { recovered = JSON.parse(W.localStorage?.getItem(recoveryKey(cid)) ?? 'null') ?? undefined; } catch { /* invalid/disabled storage */ }
        if (recovered && !await sendClock(recovered).then(answer => answer?.ok === true)) return;
        const listed = await withAsyncDeadline(async signal => {
            const response = await fetch(`${W.BASE_URL ?? ''}options/tools/campaigns/checkpoint_campaign_time_inc.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
                body: JSON.stringify({ campaign_id: cid, action: 'offline_list' }), signal });
            return await response.json() as Row;
        }, 20000);
        if (!listed.ok || !Array.isArray(listed.events)) return;
        for (const event of listed.events) {
            const campaign = heldCampaign(cid);
            if (!campaign) return;
            const request = clockRequest('offline', cid), plan = request.clock_plan as CampaignClockPlan & { offline_spans: Row[] };
            const base = ftClockBaseFromArr(campaign);
            let clock = ftDeriveCampaignClock(base, nowFor(campaign)).cols;
            plan.offline_spans = [];
            for (const segment of event.segments) {
                const seconds = Number(segment.seconds);
                if (!Number.isSafeInteger(seconds) || seconds < 0) throw Error('calendar-light:elapsed-unavailable');
                clock = ftCampaignTimeAddSeconds(clock, base.calendar, seconds);
                const fast = segment.fast_forward === true;
                plan.offline_spans.push({ clock, source: segment.source, rounds: fast ? 1 : 0, fast_forward: fast,
                    ...(fast ? {} : { elapsed_seconds: seconds }) });
                if (fast) plan.rows.push({ roll_type_1: 'campaign_downtime_time_pushed', roll_result: seconds, roll_private: 0, rolls_full: '' });
            }
            const light = campaignLightForClock(campaign, clock);
            plan.after = { ...plan.after, clock, campaign_light: light.campaign_light, campaign_sun_state: light.campaign_sun_state, sun_transition_minutes: light.transitions };
            plan.rows = plan.rows.filter(row => !row.roll_type_1.startsWith('the_sun_'));
            if (light.message && (plan.before.campaign_light !== light.campaign_light || plan.before.campaign_sun_state !== light.campaign_sun_state))
                plan.rows.push({ roll_type_1: light.message, roll_result: 0, roll_private: 0, rolls_full: '' });
            const answer = await sendClock({ ...request, event_id: event.id, generation: event.coalesced });
            if (!answer?.ok) return; // A later task replans conflicts; an unknown save retains its receipt bytes.
        }
    } catch (error) {
        if (error instanceof DOMException && error.name === 'TimeoutError') W.dispatchEvent?.(new CustomEvent('ft:time-pending-refused', {
            detail: { stage: 'elapsed-list', result: { error: 'calendar-light:elapsed-timeout' } },
        }));
        // Facts remain durable. The next scheduled adoption retries them.
    }
    finally { draining = false; }
}
Object.assign(W, {
    ftPlanCampaignClockRequest: (action: ClockAction, cid: string) => clockRequest(action, cid),
    ftSendCampaignClock: sendClock, ftTryCommitCalendarClock: commitCalendarClock, ftDrainCalendarElapsed: drainCalendarElapsed,
    ftPlanCampaignLight: (campaign: Row) => {
        const clock = ftClockColsFromArr(campaign);
        const calendar = ftClockBaseFromArr(campaign).calendar;
        if (!calendar.months.some(m => m.month_order === clock.month)) throw Error('calendar-light:calendar-unavailable');
        return campaignLightForClock(campaign, clock);
    },
});
// Adoption does no campaign re-fetch; only the raw pending-fact listing is read.
for (const name of ['ft:campaign-clock-installed', 'ft:campaign-state-received']) window.addEventListener(name, () => {
    const cid = String(W.FateTide?.campaign?.id ?? '');
    if (cid) void drainCalendarElapsed(cid);
});

if (typeof window.setInterval === 'function') window.setInterval(() => {
    const cid = String(W.FateTide?.campaign?.id ?? '');
    if (cid) void drainCalendarElapsed(cid);
}, 30_000);
